import { Button, Modal } from 'antd';
import { requestAccess } from 'api/auth.api';
import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openToast } from '@tools/ui-utils/toast.util';
import TeamRoleForm, { RequestFormValues } from './team-role.form';

type props = {
  isRequestVisible: boolean;
  onFinish: () => void;
};

const RequestAccessModal: React.FC<React.PropsWithChildren<props>> = ({
  isRequestVisible,
  onFinish,
}) => {
  const { t } = useTranslation();

  const [isSubmitted, setIsSubmitted] = useState(false);

  const onRequestAccessFinish = async (values: RequestFormValues) => {
    try {
      const { email, roleIds } = values;
      await requestAccess(email, roleIds);

      openToast({
        type: 'success',
        message: t('success'),
        description: t('auth.request.requestSent'),
      });

      onFinish();
    } catch (err) {
      const error = err as AxiosError;
      const description = error?.response?.data?.error || error?.response?.data?.message;
      openToast({
        type: 'error',
        message: t('error'),
        description,
      });
    } finally {
      setIsSubmitted(false);
    }
  };

  return (
    <Modal
      title={t('auth.request.requestAccessModal')}
      open={isRequestVisible}
      onCancel={onFinish}
      footer={[
        <Button key="back" onClick={onFinish} data-testid="permission-request-modal-cancel-button">
          {t('auth.request.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isSubmitted}
          onClick={() => setIsSubmitted(true)}
          data-testid="button-ask-permission"
        >
          {t('auth.request.requestUser')}
        </Button>,
      ]}
    >
      <TeamRoleForm
        onSubmit={onRequestAccessFinish}
        onSubmitFailed={() => setIsSubmitted(false)}
        submitted={isSubmitted}
        isNewUserRequest
      />
    </Modal>
  );
};

export default RequestAccessModal;
